import React, { useCallback, useEffect, useState } from 'react';
import { formatApiError } from '@/utils/formatApiError';

export type MaintenanceCleanupModalConfig = {
  title: string;
  confirmWarning: string;
  loadPreview: () => Promise<{ eligibleCount: number; canClear?: boolean }>;
  executeCleanup: () => Promise<{ deleted: number; executionMs?: number }>;
};

type Props = {
  config: MaintenanceCleanupModalConfig;
  onClose: () => void;
  onDone: (message: string) => void | Promise<void>;
};

type PreviewState = {
  eligibleCount: number;
  canClear: boolean;
};

export default function MaintenanceCleanupModal({ config, onClose, onDone }: Props) {
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const resetModalState = useCallback(() => {
    setPreview(null);
    setLoadingPreview(true);
    setSubmitting(false);
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError('');
    try {
      const result = await config.loadPreview();
      const eligibleCount = result.eligibleCount ?? 0;
      setPreview({
        eligibleCount,
        canClear: result.canClear ?? eligibleCount > 0,
      });
    } catch (e) {
      setError(formatApiError(e, 'Failed to load preview'));
    } finally {
      setLoadingPreview(false);
    }
  }, [config]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  async function confirm() {
    if (!preview?.canClear) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await config.executeCleanup();
      const deleted = result.deleted ?? 0;
      const message =
        deleted === 0
          ? 'No notification records to remove'
          : `Removed ${deleted} notification record(s)`;
      try {
        await Promise.resolve(onDone(message));
      } catch {
        // Parent refresh/toast is best-effort after successful cleanup.
      }
      handleClose();
    } catch (e) {
      setError(formatApiError(e, 'Cleanup failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const eligibleCount = preview?.eligibleCount ?? 0;
  const canClear = preview?.canClear ?? false;

  return (
    <div className="modal-backdrop" onClick={handleClose} role="presentation">
      <div className="modal card card-elevated" onClick={(e) => e.stopPropagation()} role="dialog">
        <h2>{config.title}</h2>
        {error ? <div className="alert alert-error">{error}</div> : null}

        <p className="modal-lead">
          {loadingPreview ? (
            'Counting matching records…'
          ) : !canClear ? (
            'No notification records are eligible for removal right now.'
          ) : (
            <>
              This will permanently remove <strong>{eligibleCount}</strong> notification record(s).
            </>
          )}
        </p>

        <p className="modal-hint">{config.confirmWarning}</p>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" disabled={submitting} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={submitting || loadingPreview || !canClear}
            onClick={() => void confirm()}
          >
            {submitting ? 'Removing…' : 'Clear notifications'}
          </button>
        </div>
      </div>
    </div>
  );
}
